import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";
import { KeyRound, MailCheck, ShieldCheck } from "lucide-react";
import { errorText } from "@/lib/calendar";
import { fmtDate } from "./format";

const CodeForm = ({ pendingEmail, demoCode, onVerified }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiClient.post("/owner/recovery-email/verify", { code });
      toast.success("Recovery email confirmed");
      setCode("");
      onVerified();
    } catch (err) {
      toast.error(errorText(err, "That code is not correct"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={verify} className="space-y-3 mt-6 border-t border-slate-100 pt-6" data-testid="owner-recovery-code-form">
      <p className="text-sm text-slate-600">Enter the 6-digit code sent to <span className="font-semibold">{pendingEmail}</span>. It expires in 10 minutes.</p>
      {demoCode && <p className="text-xs bg-lime-50 border border-lime-200 text-lime-800 rounded-lg px-3 py-2" data-testid="owner-recovery-demo-code">Preview only: code {demoCode}</p>}
      <div className="flex gap-3">
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000"
          className="max-w-[180px] text-center font-mono text-lg tracking-widest" data-testid="owner-recovery-code-input" />
        <Button type="submit" disabled={busy || code.length !== 6} className="bg-indigo-900 hover:bg-indigo-800" data-testid="owner-recovery-verify-btn">Confirm</Button>
      </div>
    </form>
  );
};

export const RecoveryEmailPanel = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => apiClient.get("/owner/recovery-email").then((r) => setInfo(r.data)).catch((e) => toast.error(errorText(e, "Could not load")));
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await apiClient.post("/owner/recovery-email", { email, current_password: password });
      toast.success(`Code sent to ${data.sent_to}`);
      setDemoCode(data.demo_code || "");
      setPassword("");
      load();
    } catch (err) {
      toast.error(errorText(err, "Could not send the code"));
    } finally {
      setBusy(false);
    }
  };

  if (!info) return <p className="text-slate-500 py-12">Loading…</p>;

  return (
    <div className="grid lg:grid-cols-5 gap-8" data-testid="owner-recovery-panel">
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-indigo-950 text-white p-6">
          <ShieldCheck className="w-8 h-8 text-lime-400 mb-3" />
          <h3 className="font-heading text-xl font-bold mb-2">How your Owner account is protected</h3>
          <ul className="text-sm text-indigo-100 space-y-2 list-disc pl-4">
            <li>Only this account can be the Owner. No other account can be promoted to Owner from inside the app.</li>
            <li>No one can suspend it, reset it, or send it a reset link. That includes you.</li>
            <li>Forgot password emails your code to your sign-in email and to your confirmed recovery email.</li>
          </ul>
          <Button onClick={() => navigate("/change-password")} className="mt-5 bg-lime-400 text-slate-900 hover:bg-lime-300 font-bold" data-testid="owner-change-password-btn">
            <KeyRound className="w-4 h-4 mr-2" /> Change my password
          </Button>
        </div>
      </div>
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start gap-3 mb-6">
          <MailCheck className="w-6 h-6 text-indigo-900 mt-0.5" />
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-500 font-semibold">Recovery email</p>
            <p className="font-heading text-lg font-bold text-slate-900 break-all" data-testid="owner-recovery-current">{info.email || "Not set yet"}</p>
            {info.verified_at && <p className="text-xs text-slate-500">Confirmed {fmtDate(info.verified_at)}</p>}
          </div>
        </div>
        <form onSubmit={send} className="space-y-4" data-testid="owner-recovery-form">
          <div className="space-y-2">
            <Label htmlFor="recovery-email">{info.email ? "Replace with" : "Recovery email address"}</Label>
            <Input id="recovery-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="another-address@example.org" data-testid="owner-recovery-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="recovery-password">Your current password</Label>
            <PasswordInput id="recovery-password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="owner-recovery-password-input"
              toggleClassName="text-slate-500 hover:text-slate-800 focus-visible:text-indigo-600" />
          </div>
          <Button type="submit" disabled={busy} className="bg-indigo-900 hover:bg-indigo-800" data-testid="owner-recovery-send-btn">
            {busy ? "Sending…" : "Send confirmation code"}
          </Button>
        </form>
        {info.pending_email && <CodeForm pendingEmail={info.pending_email} demoCode={demoCode} onVerified={() => { setDemoCode(""); setEmail(""); load(); }} />}
      </div>
    </div>
  );
};
